import * as React from 'react';
import { AdminUserSearchResultExtended, DomainWithFilespaces } from 'fs-api-client';
import {
    copyToClipboard,
    formatEpochTimeInMiliSecondsToReadable,
    formatEpochTimeInSecondsToReadable
} from '../../../webportal/utils/utilFunctions';
import {
    CheckMarkIcon,
    EmailIcon,
    renderCheckMarkIcon,
    renderCopyIcon,
    renderCrossIcon,
    renderWaitingForSuccessElement,
} from '../../../webportal/components/common/Icons';
import PaymentMethodsEnum = AdminUserSearchResultExtended.PaymentMethodsEnum;
import { createDefaultApi } from '../../../webportal/utils/createApi';
import { ClientResponse } from 'http';

const TIMEOUT_MS = 5000;

interface BackOfficeUserDetailsState {
    isCopyButtonClicked: boolean;
    isChangeEmailModalShown: boolean;
    isConfirmEmailChangeModalShown: boolean;
    areConfirmationButtonsShown: boolean;
    updateEmailOperationNotStarted: boolean;
    updateEmailOperationStarted: boolean;
    updateEmailOperationFinished: boolean;
    errorMessage: string;
    newUserEmail: string;
    currentUserEmail: string;
}

interface BackOfficeUserDetailsProps {
    user?: AdminUserSearchResultExtended;
    userExtra?: AdminUserSearchResultExtended;
    fetchUsers?: () => Promise<void>;
    domain?: DomainWithFilespaces;
    securityScopes: string[];
}

type ResponseError = {
    body: {
        message: string
    }
    response: object;
};

export class UserDetails extends React.Component<BackOfficeUserDetailsProps, BackOfficeUserDetailsState> {
    constructor(props: BackOfficeUserDetailsProps) {
        super(props);
        this.renderUserSearchResult = this.renderUserSearchResult.bind(this);
        this.renderApiElement = this.renderApiElement.bind(this);
        this.renderUserStatus = this.renderUserStatus.bind(this);
        this.renderTrialElement = this.renderTrialElement.bind(this);
        this.renderCardElement = this.renderCardElement.bind(this);
        this.renderAltPaymentElement = this.renderAltPaymentElement.bind(this);
        this.renderEmailUpdateModal = this.renderEmailUpdateModal.bind(this);
        this.renderEmailUpdateConfirmationModal = this.renderEmailUpdateConfirmationModal.bind(this);
        this.renderConfirmButton = this.renderConfirmButton.bind(this);
        this.renderCancelButton = this.renderCancelButton.bind(this);
        this.renderSuccessElement = this.renderSuccessElement.bind(this);
        this.handleEmailUpdateOperation = this.handleEmailUpdateOperation.bind(this);
        this.updateEmail = this.updateEmail.bind(this);
        this.errorHandler = this.errorHandler.bind(this);
        this.checkForRequiredSecurityScopeForSuspend = this.checkForRequiredSecurityScopeForSuspend.bind(this);
        this.isSocialLogin = this.isSocialLogin.bind(this);
        this.validateChangeEmailInputField = this.validateChangeEmailInputField.bind(this);
        this.cancelButtonEmailChangeCallback = this.cancelButtonEmailChangeCallback.bind(this);
        this.cancelButtonEmailConfirmCallback = this.cancelButtonEmailConfirmCallback.bind(this);
        this.errorMessageTimeout = this.errorMessageTimeout.bind(this);
        this.validateEmail = this.validateEmail.bind(this);
        this.renderUserEmailInEditMode = this.renderUserEmailInEditMode.bind(this);
        this.onSuccessfulEmailUpdateOperation = this.onSuccessfulEmailUpdateOperation.bind(this);
        this.onFailedEmailUpdateOperation = this.onFailedEmailUpdateOperation.bind(this);
        this.onValidEmailUpdateInputField = this.onValidEmailUpdateInputField.bind(this);
        this.setChangeEmailError = this.setChangeEmailError.bind(this);

        this.state = {
            isCopyButtonClicked: false,
            isChangeEmailModalShown: false,
            isConfirmEmailChangeModalShown: false,
            areConfirmationButtonsShown: true,
            updateEmailOperationNotStarted: true,
            updateEmailOperationStarted: false,
            updateEmailOperationFinished: false,
            currentUserEmail: this.props.user?.email as string,
            errorMessage: '',
            newUserEmail: '',
        };
    }

    render() {
        return (
            <div className="row">
                {this.renderUserSearchResult()}
            </div>
        );
    }

    private renderUserSearchResult(): JSX.Element | null {
        let copyClassName = this.state.isCopyButtonClicked ? 'copy copied' : 'copy';

        if (!this.props.user || !this.props.userExtra) {
            return null;
        }

        const userSearchResult = this.props.user;
        let userName;

        if (this.props.userExtra.givenName && this.props.userExtra.familyName) {
            userName = `${this.props.userExtra.givenName} ${this.props.userExtra.familyName}`;
        }

        return (
            <div key={userSearchResult.id}>
                <h2>
                    {this.renderUserEmailInEditMode(() => {
                        this.setState({ isChangeEmailModalShown: true });
                    })}
                    {(!this.checkForRequiredSecurityScopeForSuspend() || this.isSocialLogin()) && this.props.user?.email}
                    <a
                        href="javascript:void(0);"
                        className={copyClassName}
                        title="Copy to clipboard"
                        onClick={() => {
                            this.setState({ isCopyButtonClicked: true });
                            copyToClipboard(userSearchResult.email);
                        }}
                    >
                        {renderCopyIcon()}
                    </a>
                    {this.state.isChangeEmailModalShown && this.renderEmailUpdateModal()}
                    {this.state.isConfirmEmailChangeModalShown && this.renderEmailUpdateConfirmationModal()}
                </h2>
                <ul className="details">
                    <li
                        className={userSearchResult.isSuspended ? 'warning' : 'active'}
                    >
                        {
                            this.renderUserStatus(userSearchResult)
                        }
                    </li>
                    <li title="Identity provider">{userSearchResult.identityProviders}</li>
                    {this.props.domain && <li title="Domain">{this.props.domain.name}</li>}
                </ul>
                <div className="description">
                    <table>
                        <tbody>
                        <tr>
                            <td className="tableLabel">Full name:</td>
                            <td className="tableSpace">&nbsp;</td>
                            {userName && <td>{userName}</td>}
                            {!userName && renderCrossIcon()}
                        </tr>
                        <tr>
                            <td className="tableLabel">Company name:</td>
                            <td className="tableSpace">&nbsp;</td>
                            {this.props.userExtra.companyName && <td>{this.props.userExtra.companyName}</td>}
                            {!this.props.userExtra.companyName && renderCrossIcon()}
                        </tr>
                        <tr>
                            <td className="tableLabel">Phone number:</td>
                            <td className="tableSpace">&nbsp;</td>
                            {this.props.userExtra.phoneNumber && <td>{this.props.userExtra.phoneNumber}</td>}
                            {!this.props.userExtra.phoneNumber && renderCrossIcon()}
                        </tr>
                        <tr>
                            <td className="tableLabel">Created:</td>
                            <td className="tableSpace">&nbsp;</td>
                            <td>{formatEpochTimeInMiliSecondsToReadable(userSearchResult.created)}</td>
                        </tr>
                        <tr>
                            <td className="tableLabel">Trial:</td>
                            <td className="tableSpace">&nbsp;</td>
                            {this.renderTrialElement()}
                        </tr>
                        <tr>
                            <td className="tableLabel">CC on file:</td>
                            <td className="tableSpace">&nbsp;</td>
                            {this.renderCardElement(this.props.userExtra)}
                        </tr>
                        <tr>
                            <td className="tableLabel">Alt payment on file</td>
                            <td className="tableSpace">&nbsp;</td>
                            {this.renderAltPaymentElement(this.props.userExtra)}
                        </tr>
                        <tr>
                            <td className="tableLabel">API key:</td>
                            <td className="tableSpace">&nbsp;</td>
                            {this.renderApiElement(userSearchResult)}
                        </tr>
                        <tr>
                            <td className="tableLabel">Webportal ID:</td>
                            <td className="tableSpace">&nbsp;</td>
                            {userSearchResult.id}
                            <td>
                                <a
                                    href="javascript:void(0);"
                                    className={copyClassName}
                                    title="Copy to clipboard"
                                    onClick={() => {
                                    this.setState({ isCopyButtonClicked: true });
                                    copyToClipboard(userSearchResult.id);
                                }}
                                >
                                {renderCopyIcon()}
                                </a>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private renderApiElement(userSearchResult: AdminUserSearchResultExtended): JSX.Element {
        if (userSearchResult.hasApiKey) {
            return renderCheckMarkIcon();
        }
        return renderCrossIcon();
    }

    private checkForRequiredSecurityScopeForSuspend(): boolean {
        return this.props.securityScopes.includes('adminWrite') || this.props.securityScopes.includes('master');
    }

    private renderUserStatus(userSearchResult: AdminUserSearchResultExtended): string {
        if (!userSearchResult.isSuspended) {
            return 'Active';
        }
        return 'Suspended';
    }

    private renderTrialElement(): JSX.Element {
        if (this.props.user && this.props.userExtra) {
            const result = formatEpochTimeInSecondsToReadable(this.props.userExtra.trialEndDate!);
            if (this.props.userExtra.trialEndDate && this.props.user.isAccountTrial) {
                return (
                    <React.Fragment>
                        <td>
                            By {result}
                        </td>
                    </React.Fragment>
                );
            }
        }
        return (
            <React.Fragment>
                {renderCrossIcon()}
            </React.Fragment>
        );
    }

    private renderCardElement(userSearchResult: AdminUserSearchResultExtended): JSX.Element {
        if (userSearchResult.paymentMethods?.includes(PaymentMethodsEnum.CreditCard)) {
            return (
                <React.Fragment>
                    {renderCheckMarkIcon()}
                </React.Fragment>
            );
        }
        return (
            <React.Fragment>
                {renderCrossIcon()}
            </React.Fragment>
        );
    }

    private renderAltPaymentElement(userSearchResult: AdminUserSearchResultExtended): JSX.Element {
        if (userSearchResult.paymentMethods?.includes(PaymentMethodsEnum.WireOrCheck)) {
            return (
                <React.Fragment>
                    {renderCheckMarkIcon()}
                </React.Fragment>
            );
        }

        if (userSearchResult.paymentMethods?.includes(PaymentMethodsEnum.Invalid)) {
            return (
                <React.Fragment>
                    <td>
                        Invalid payment method Stripe metadata
                    </td>
                </React.Fragment>
            );
        }

        return (
            <React.Fragment>
                {renderCrossIcon()}
            </React.Fragment>
        );
    }

    private renderEmailUpdateModal(): JSX.Element {
        const modalClassName = this.state.isChangeEmailModalShown ? 'modalBO show' : 'modalBO';
        return (
            <div
                className={modalClassName}
                id="confirmModal"
                tabIndex={-1}
                role="dialog"
                aria-labelledby="confirmModal"
                aria-hidden="true"
            >
                <div>
                    <header className="center">
                        <EmailIcon className="icon" width="27px"/>
                        <h2>Change account's email</h2>
                    </header>
                    <section>
                        <p>Please confirm the <b>new</b> email of the user.</p>
                        {this.state.errorMessage && this.errorHandler(this.state.errorMessage)}
                        <input
                            id="email-input"
                            data-autocomplete="off"
                            type="email"
                            required={true}
                            value={this.state.newUserEmail}
                            onChange={(event) => {
                                this.setState({ newUserEmail: event.target.value });
                            }}
                        />
                    </section>
                    <footer>
                        {this.renderConfirmButton(this.validateChangeEmailInputField)}
                        {this.renderCancelButton(() => {
                            this.cancelButtonEmailChangeCallback();
                        })}
                    </footer>
                </div>
            </div>
        );
    }

    private renderEmailUpdateConfirmationModal(): JSX.Element {
        const modalClassName = this.state.isConfirmEmailChangeModalShown ? 'modalBO show' : 'modalBO';
        return (
            <div
                className={modalClassName}
                id="confirmModal"
                tabIndex={-1}
                role="dialog"
                aria-labelledby="confirmModal"
                aria-hidden="true"
            >
                <div style={{ textAlign: 'center' }}>
                    <header>
                        <EmailIcon className="icon" width="27px"/>
                        <h2> Are you sure you want to change <br/> the account's email?</h2>
                    </header>
                    Current email:
                    <strong>{this.state.currentUserEmail.toLowerCase()}</strong>
                    <br/>
                    New email:
                    <strong>{this.state.newUserEmail.toLowerCase()}</strong>
                    <div style={{ marginTop: '20px' }}>
                        {
                            this.state.updateEmailOperationNotStarted &&
                            this.renderConfirmButton(this.handleEmailUpdateOperation)
                        }
                        {
                            this.state.updateEmailOperationNotStarted &&
                            this.renderCancelButton(() => {
                                this.cancelButtonEmailConfirmCallback();
                            })
                        }
                        {
                            this.state.updateEmailOperationStarted &&
                            renderWaitingForSuccessElement('Starting an email update operation...')
                        }
                        <footer>
                            {this.state.updateEmailOperationFinished && this.renderSuccessElement()}
                            {this.state.errorMessage && this.errorHandler(this.state.errorMessage)}
                            {(this.state.updateEmailOperationFinished || this.state.errorMessage) &&
                                <div style={{ marginTop: '20px' }}>
                                    {this.renderCancelButton(() => {
                                                                 this.cancelButtonEmailConfirmCallback();
                                                             },
                                                             'Close')}
                                </div>
                            }
                        </footer>
                    </div>
                </div>

            </div>
        );
    }

    private renderSuccessElement(): JSX.Element {
        return (
            <footer>
                <div className="icon active">
                    <CheckMarkIcon width="28px" height="20px"/>
                </div>
                <span className="success">
                    Done
                </span>
            </footer>
        );
    }

    private renderUserEmailInEditMode(callback: any): JSX.Element {
        return (
            <React.Fragment>
                {
                    (this.checkForRequiredSecurityScopeForSuspend() && !this.isSocialLogin()) &&
                    <a
                        className="edit"
                        href="javascript:void(0)"
                        onClick={() => {
                            callback();
                        }}
                    >
                        {this.state.currentUserEmail.toLowerCase()}
                    </a>
                }
            </React.Fragment>
        );
    }

    private renderConfirmButton(callback: any): JSX.Element {
        return (
            <button
                className="btn"
                style={{ marginTop: '10px' }}
                onClick={async () => {
                    await callback();
                }}
            >
                Confirm
            </button>
        );
    }

    private renderCancelButton(callback: any, buttonName: string = 'Cancel'): JSX.Element {
        return (
            <a
                data-dismiss="modal"
                className="close"
                aria-label="close"
                href="javascript:void(0)"
                style={{ marginTop: '10px' }}
                onClick={(event) => {
                    callback();
                }}
            >
                {buttonName}
            </a>
        );
    }

    private async handleEmailUpdateOperation(): Promise<void> {
        try {
            this.setState({ updateEmailOperationStarted: true, updateEmailOperationNotStarted: false });

            const { response } = await this.updateEmail();
            if (response.statusCode === 201) {
                this.onSuccessfulEmailUpdateOperation();
            }

        } catch (e) {
            this.onFailedEmailUpdateOperation(e);
        }
    }

    private async updateEmail(): Promise<{ response: ClientResponse, body?: any }> {
        const email = this.state.newUserEmail.trim().toLowerCase();
        const userId = this.props.user?.id;
        const api = await createDefaultApi();
        const response = await api.updateUserEmail(userId as string, { email });

        return response;
    }

    private errorHandler(e: string): JSX.Element {
        return (
            <p style={{ color: 'orange' }}>
                {e}
            </p>
        );
    }

    private isSocialLogin(): boolean {
        return this.props.user?.identityProviders !== 'Cognito';
    }

    private validateChangeEmailInputField(): void {
        const newEmailTrimmed = this.state.newUserEmail.trim();
        const currentUserEmail = this.state.currentUserEmail;

        if (newEmailTrimmed === '') {
            this.setChangeEmailError('Input field must not be empty');
            return;
        }

        if (newEmailTrimmed === currentUserEmail) {
            this.setChangeEmailError('New email address cannot be equal to the current email address');
            return;
        }

        if (!this.validateEmail(newEmailTrimmed)) {
            this.setChangeEmailError('Email must be written in valid format');
            return;
        }

        this.onValidEmailUpdateInputField();
    }

    private validateEmail(value: string): boolean {
        const expression = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return expression.test(value);
    }

    private cancelButtonEmailChangeCallback(): void {
        this.setState({ isChangeEmailModalShown: false, newUserEmail: '', errorMessage: '' });
    }

    private setChangeEmailError(message: string): void {
        this.setState({ errorMessage: message });
        this.errorMessageTimeout(TIMEOUT_MS);
    }

    private cancelButtonEmailConfirmCallback(): void {
        this.setState(
            {
                isChangeEmailModalShown: false,
                isConfirmEmailChangeModalShown: false,
                updateEmailOperationNotStarted: true,
                errorMessage: '',
                newUserEmail: ''
            });
    }

    private onSuccessfulEmailUpdateOperation(): void {
        this.setState(
            {
                updateEmailOperationStarted: false,
                updateEmailOperationFinished: true,
                currentUserEmail: this.state.newUserEmail
            }
        );

        setTimeout(
            () => {
                this.setState(
            {
                    isConfirmEmailChangeModalShown: false,
                    updateEmailOperationNotStarted: true,
                    updateEmailOperationStarted: false,
                    updateEmailOperationFinished: false,
                    newUserEmail: ''
                }
            );
            },
            TIMEOUT_MS);
    }

    private onFailedEmailUpdateOperation(e: ResponseError): void {
        this.setState(
            {
                errorMessage: e?.body?.message,
                updateEmailOperationFinished: false,
                updateEmailOperationStarted: false
            });
        setTimeout(() => {
            this.setState(
                {
                    isConfirmEmailChangeModalShown: false,
                    errorMessage: '',
                    newUserEmail: ''
                });
        },
                   TIMEOUT_MS);
    }

    private onValidEmailUpdateInputField(): void {
        this.setState(
            {
                isConfirmEmailChangeModalShown: true,
                isChangeEmailModalShown: false,
                updateEmailOperationStarted: false,
                updateEmailOperationNotStarted: true,
                errorMessage: ''
            });
    }

    private errorMessageTimeout(timeout: number): void {
        setTimeout(() => {
            this.setState(
                {
                    errorMessage: '',
                    updateEmailOperationNotStarted: true,
                    updateEmailOperationStarted: false,
                    updateEmailOperationFinished: false
                });
        },
                   timeout);
    }

}



// WEBPACK FOOTER //
// ./src/backoffice/components/user/UserDetails.tsx